import { getRecaptchaSiteKey, getFormValidators } from '../helpers/api';
import I18n from '../helpers/i18n';
import { translations } from '../translations/translations';

export class FormGenerator {
  constructor(target) {
    this.section = target;
    this.form = this.section.querySelector('form');

    this.submit = this.form && this.form.querySelector('.field.submit');
    this.resetBtn = this.form && this.form.querySelector('[data-reset]');
    this.captchaField = this.form.querySelector('#g-recaptcha-response');

    this.successBox = this.section.querySelector('.success-box');
    this.errorBox = this.section.querySelector('.error-box');

    this.validators = null;
    this.validated = false;
    this.recaptchaSiteKey = null;
    this.i18n = new I18n(translations.formGenerator);
  }

  async init() {
    const visibleSuccess = this.successBox && !this.successBox.classList.contains('hide');
    const visibleError = this.errorBox && !this.errorBox.classList.contains('hide');

    if (visibleSuccess || visibleError) {
      this.section.scrollIntoView();
    }

    if (!this.form) {
      this.afterInit();

      return;
    }

    await this.getValidators();
    this.onSubmit();
    this.onReset();
    this.onBlur();
    this.onCheckboxChange();
    this.onSelectChange();
    this.afterInit();
    this.recaptchaSiteKey = await getRecaptchaSiteKey();
  }

  loading () {}

  onBlur() {
    for (const input of this.form.elements) {
      input.addEventListener('blur', (e) => {
        const { validationMessage } = this.validateInput({ input: e.currentTarget });

        this.handleInputError(input, validationMessage);
      });
    }
  }

  onCheckboxChange() {
    this.form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      input.addEventListener('change', (e) => {
        const { validationMessage } = this.validateInput({ input: e.currentTarget });

        this.handleInputError(input, validationMessage);
      });
    })
  }

  onSelectChange() {
    this.section.querySelectorAll('div.cs-select').forEach(el => {
      const select = el.querySelector('select');

      select.addEventListener('change', (e) => {
        const {validationMessage} = this.validateInput({input: e.currentTarget});

        this.handleInputError(select, validationMessage);
      })
    })
  }

  afterInit() {}

  async getValidators() {
    this.validators = await getFormValidators();
  }

  onSubmit() {
    this.form.addEventListener('submit', (e) => {
      this.submit.classList.add('loading');

      if (!this.validated) {
        this.submit.classList.remove('loading');

        e.preventDefault();
        this.submit.classList.add('loading');
        const isValid = this.validate();

        if (!isValid) {
          this.handleGlobalError();
          this.scrollToFirstError();

          return;
        }

        this.captchaField = this.form.querySelector('#g-recaptcha-response');
        this.validated = this.captchaField ? Boolean(this.captchaField.value) : true;

        if (!this.validated) {
          return;
        }

        this.form.submit();
      }
    });
  }

  scrollToFirstError() {
    const firstError = this.section.querySelector('.validation-error');
    const errorField = firstError.closest('.field');
    const el = errorField ? errorField : this.section;

    setTimeout(() => {
      el.scrollIntoView();
    });
  }

  validate() {
    this.resetAllErrors();

    const regexValid = this.ruleValidate();
    const checkboxValid = this.validateCheckboxes();

    return regexValid && checkboxValid;
  }

  validateCheckboxes() {
    const checkboxes = this.form.querySelectorAll('[type="checkbox"]');
    let isValid = true;

    if (!checkboxes.length) {
      return true;
    }

    const checkedStates = {};

    for (let i = 0; i < checkboxes.length; i++) {
      const checkbox = checkboxes[i];
      const isChecked = checkbox.checked;

      checkedStates[checkbox.name] = checkedStates[checkbox.name] || isChecked;
    }

    // eslint-disable-next-line guard-for-in
    for (const key in checkedStates) {
      const checked = checkedStates[key];
      const currentCheckbox = this.form.querySelector(`[name="${key}"]`);
      const fieldWrapper = currentCheckbox.closest('.field');

      if (currentCheckbox.required && !checked) {
        const errorWrapper = fieldWrapper.querySelector('.validation-error');

        this.submit.classList.remove('loading');
        errorWrapper.innerHTML = this.i18n.get('checkboxErrorMsg');
        fieldWrapper.classList.add('has-error');
        isValid = false;
      }
    }

    return isValid;
  }

  ruleValidate() {
    if (!this.validators && this.validators.nodes) {
      return true;
    }

    let valid = true;

    for (const input of this.form.elements) {
      const {
        valid: _valid,
        currValid = true,
        validationMessage = ''
      } = this.validateInput({
        input,
        valid
      });

      valid = valid && _valid;

      if (!currValid) {
        this.handleInputError(input, validationMessage);
      }
    }

    return valid;
  }

  validateInput({ input, _valid = true }) {
    let valid = _valid;
    let currValid = true;
    let validationMessage = '';

    if (input.required && !input.value) {
      currValid = false;
      valid = false;
      validationMessage = this.i18n.get('requiredErrorMsg');
    } else if (!input.required && !input.value && !input.dataset.validation && !input.pattern) {
      currValid = true;
    } else if (input.dataset.validation) {
      const rules = input.dataset.validation.split(',');
      const regexList = rules.map(el => this.getValidatorRegex(el));

      regexList.map(regex => {
        if (regex) {
          currValid = regex.test(input.value);
          valid = currValid && valid;
        }

        if (!currValid) {
          validationMessage = `${input.dataset.validation}: ${this.i18n.get('fieldErrorMsg')}`;
        }
      });
    }

    return { valid, currValid, validationMessage };
  }

  getValidatorRegex(validationName) {
    const matchedRules = this.validators.nodes.filter(el => el.name === validationName);
    const ruleObj = matchedRules.length ? matchedRules[0] : {};
    const matchedExpression = ruleObj.properties.filter(el => el.name === 'expression');
    const expression = matchedExpression.length ? matchedExpression[0] : {};
    const regexStr = expression.values ? expression.values[0] : '';
    let regex = /.*/gu;

    let isValidRegex = true;

    try {
      // eslint-disable-next-line require-unicode-regexp
      regex = new RegExp(regexStr);
    } catch (e) {
      isValidRegex = false;
    }

    if (validationName === 'noHTML' && (!expression.values || !expression.values.length)) {
      isValidRegex = true;
      regex = /^[^<>]+$/ui;
    }

    return regex && isValidRegex ? regex : '';
  }

  handleGlobalError(msg = '') {
    this.submit.classList.remove('loading');

    if (this.errorBox && !this.errorBox.classList.contains('newsletter-sent-error')) {
      this.errorBox.classList.remove('hide');
      this.errorBox.querySelector('.custom-error-message').innerHTML = msg;
    }

    setTimeout(() => {
      this.section.scrollIntoView();
    });
  }

  resetAllErrors() {
    const errorContainer = this.form.querySelectorAll('.validation-error');

    // eslint-disable-next-line no-return-assign
    errorContainer.forEach(el => {
      el.innerHTML = '';
      el.parentElement.classList.remove('has-error')
    });

    if (this.errorBox && !this.errorBox.classList.contains('newsletter-sent-error')) {
      this.errorBox.querySelector('.custom-error-message').innerHTML = '';
      this.errorBox.classList.add('hide');
    }
  }

  handleInputError(input, validationMessage) {
    const fieldWrapper = input.closest('.field');
    const errorContainer = fieldWrapper.querySelector('.validation-error');

    if (errorContainer) {
      errorContainer.innerHTML = validationMessage;
      // eslint-disable-next-line no-unused-expressions
      validationMessage ? fieldWrapper.classList.add('has-error') : fieldWrapper.classList.remove('has-error');
    }
  }

  onReset() {
    // eslint-disable-next-line no-unused-expressions
    this.resetBtn && this.resetBtn.addEventListener('click', (e) => {
      e.preventDefault();
      this.form.reset();
      this.reInitSelects();
      this.resetAllErrors();
    });
  }

  reInitSelects() {
    this.section.querySelectorAll('div.cs-select').forEach(el => {
      const select = el.querySelector('select');

      select.value = '';
      el.replaceWith(select);
    });

    this.section.querySelectorAll('.cs-select').forEach(select => {
      new SelectFx(select, {
        stickyPlaceholder: false,
        onChange: () => {
          select.dispatchEvent(new Event('change'));
        }
      });
    });
  }
}
